import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Clock, ArrowRight, MessageCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { ordersApi } from '@/services/api';
import { toast } from 'sonner';
import OrderLayout from './OrderLayout';
import { useOrderState } from './useOrderState';

const TIME_SLOTS = ['09:00', '11:00', '13:00', '15:00', '17:00'];

const inputCls =
  'w-full px-4 py-3 border border-charcoal/15 bg-white/70 text-charcoal focus:outline-none focus:border-gold transition-colors';

const InstallationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { order, localData, loading } = useOrderState();
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({ date: '', time_slot: '', notes: '' });

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!loading && !localData) navigate('/start');
    // If already scheduled, show confirmation
    if (!loading && order?.installation?.date && order?.current_step === 6) {
      setConfirmed(true);
      setForm({ date: order.installation.date, time_slot: order.installation.time_slot || '', notes: '' });
    }
  }, [loading, localData, order, navigate]);

  if (loading) {
    return (
      <OrderLayout currentStep={6}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      </OrderLayout>
    );
  }

  const phone = localData?.customerPhone || '';
  const waLink = confirmed
    ? `https://wa.me/${phone.replace(/[^\d+]/g, '')}?text=${encodeURIComponent(
        `استفسار عن موعد تركيب مطبخي — ${localData?.orderNumber || ''}`
      )}`
    : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time_slot) {
      toast.error(t('order.error'));
      return;
    }
    setSubmitting(true);
    try {
      await ordersApi.updateStep(localData.orderId, 6, form);
      setConfirmed(true);
      toast.success(t('order.saved'));
    } catch {
      toast.error(t('order.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <OrderLayout currentStep={6} orderNumber={localData?.orderNumber}>
        <div className="text-center py-8 space-y-6">
          <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="text-gold" size={40} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-charcoal mb-2">{t('order.installation.confirmTitle')}</h3>
            <p className="text-charcoal/60">{t('order.installation.confirmDesc')}</p>
          </div>

          {form.date && (
            <div className="bg-white/60 border border-charcoal/8 p-6 text-start max-w-xs mx-auto space-y-3">
              <div>
                <p className="text-xs text-charcoal/40 uppercase tracking-widest mb-1">
                  {t('order.installation.yourDate')}
                </p>
                <p className="font-bold text-charcoal text-lg" dir="ltr">{form.date}</p>
              </div>
              {form.time_slot && (
                <div>
                  <p className="text-xs text-charcoal/40 uppercase tracking-widest mb-1">
                    {t('order.installation.yourTime')}
                  </p>
                  <p className="font-bold text-charcoal" dir="ltr">
                    {t(`order.form.times.${form.time_slot}`)}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white font-bold px-6 py-3 hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={18} />
              {t('order.installation.contactUs')}
            </a>
            <button
              onClick={() => navigate('/client-dashboard')}
              className="inline-flex items-center justify-center gap-2 border-2 border-charcoal/15 text-charcoal font-bold px-6 py-3 hover:border-charcoal/30 transition-colors"
            >
              {t('order.installation.dashboard')}
            </button>
          </div>
        </div>
      </OrderLayout>
    );
  }

  return (
    <OrderLayout currentStep={6} orderNumber={localData?.orderNumber}>
      <form onSubmit={handleSubmit} className="space-y-7">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-charcoal/70 mb-2">
              <CalendarDays size={14} />
              {t('order.form.date')} <span className="text-gold">*</span>
            </label>
            <input
              type="date"
              value={form.date}
              min={today}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              required
              className={inputCls}
              dir="ltr"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-charcoal/70 mb-2">
              <Clock size={14} />
              {t('order.form.timeSlot')} <span className="text-gold">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, time_slot: slot }))}
                  className={`py-2.5 text-sm font-medium border transition-all ${
                    form.time_slot === slot
                      ? 'border-gold bg-gold text-charcoal-deep'
                      : 'border-charcoal/15 bg-white/60 text-charcoal/70 hover:border-gold/40'
                  }`}
                >
                  {t(`order.form.times.${slot}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-charcoal/70 mb-2">{t('order.form.notes')}</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            rows={3}
            className={`${inputCls} resize-none`}
            placeholder={t('order.form.notes')}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {submitting ? (
            <><Loader2 size={18} className="animate-spin" /> {t('order.form.saving')}</>
          ) : (
            <><CheckCircle2 size={18} /> {t('order.form.submit')}</>
          )}
        </button>
      </form>
    </OrderLayout>
  );
};

export default InstallationPage;
